import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import {
    Home as RoofingIcon,
    Construction as RemodelIcon,
    Architecture as NewConstructionIcon,
    Deck as OutdoorLivingIcon
} from '@mui/icons-material';

const COLORS = {
    black: '#000000',          // Primary black
    darkGray: '#1A1A1A',       // Slightly softer black for backgrounds
    mediumGray: '#333333',     // For text and secondary elements
    lightGray: '#666666',      // For less emphasis text
    orange: '#FF5722',         // Primary orange accent
    lightOrange: '#FF7A50',    // Hover state orange
    background: '#0A0A0A'      // Dark background
};

const services = [
    {
        id: 'roofing',
        title: 'Premium Roofing',
        description: 'Expert installation of high-quality roofing systems. From architectural shingles to metal roofing, we deliver durability and style.',
        icon: <RoofingIcon />,
        features: [
            'Architectural Shingles',
            'Metal Roofing Systems',
            'Tile & Slate Options',
            'Commercial Roofing',
            'Emergency Repairs',
            'Preventive Maintenance'
        ],
        color: COLORS.orange
    },
    {
        id: 'remodels',
        title: 'Custom Remodels',
        description: 'Transform your space with our comprehensive remodeling services. From kitchens to full home renovations.',
        icon: <RemodelIcon />,
        features: [
            'Kitchen Remodeling',
            'Bathroom Updates',
            'Room Additions',
            'Basement Finishing',
            'Interior Renovations',
            'Custom Cabinetry'
        ],
        color: COLORS.orange
    },
    {
        id: 'new-construction',
        title: 'New Construction',
        description: 'Building your dream from the ground up. Expert construction services with attention to every detail.',
        icon: <NewConstructionIcon />,
        features: [
            'Custom Home Building',
            'Commercial Construction',
            'Project Management',
            'Design Services',
            'Permit Handling',
            'Quality Assurance'
        ],
        color: COLORS.orange
    },
    {
        id: 'outdoor',
        title: 'Outdoor Living',
        description: 'Create your perfect outdoor space with custom designs that extend your living area into nature.',
        icon: <OutdoorLivingIcon />,
        features: [
            'Custom Decks & Patios',
            'Outdoor Kitchens',
            'Fire Features',
            'Pergolas & Gazebos',
            'Landscape Design',
            'Outdoor Lighting'
        ],
        color: COLORS.orange
    }
];

const Services = () => {
    return (
        <ServicesContainer>
            <ServicesHeader>
                <Title>Our Services</Title>
                <Subtitle>Expert craftsmanship for every project</Subtitle>
            </ServicesHeader>

            <ServicesGrid>
                {services.map((service) => (
                    <ServiceCard
                        key={service.id}
                        as={motion.div}
                        whileHover={{ y: -10 }}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <IconContainer style={{ background: service.color }}>
                            {service.icon}
                        </IconContainer>
                        <ServiceTitle>{service.title}</ServiceTitle>
                        <ServiceDescription>{service.description}</ServiceDescription>
                        <FeaturesList>
                            {service.features.map((feature, index) => (
                                <FeatureItem
                                    key={index}
                                    as={motion.li}
                                    whileHover={{ x: 5 }}
                                >
                                    • {feature}
                                </FeatureItem>
                            ))}
                        </FeaturesList>
                        <LearnMoreButton
                            as={motion.button}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            style={{ background: service.color }}
                        >
                            Get Started
                        </LearnMoreButton>
                    </ServiceCard>
                ))}
            </ServicesGrid>
        </ServicesContainer>
    );
};

const ServicesContainer = styled.div`
    padding: 6rem 2rem;
    background: ${COLORS.background};
    color: white;
`;

const ServicesHeader = styled.div`
    text-align: center;
    margin-bottom: 4rem;
`;

const Title = styled.h1`
    color: white;
    font-size: 2.5rem;
    margin-bottom: 1rem;
    text-transform: uppercase;
    letter-spacing: 1px;

    &:after {
        content: '';
        display: block;
        width: 80px;
        height: 4px;
        background: ${COLORS.orange};
        margin: 1rem auto 0;
    }
`;

const Subtitle = styled.p`
    color: ${COLORS.lightGray};
    font-size: 1.2rem;
`;

const ServicesGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 2rem;
    max-width: 1200px;
    margin: 0 auto;
`;

const ServiceCard = styled.div`
    background: ${COLORS.darkGray};
    border-radius: 16px;
    padding: 2rem;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    border: 1px solid ${COLORS.mediumGray};
    transition: all 0.3s ease;

    &:hover {
        border-color: ${COLORS.orange};
        transform: translateY(-5px);
    }
`;

const IconContainer = styled.div`
    width: 60px;
    height: 60px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1.5rem;
    background: ${COLORS.orange};
    transition: all 0.3s ease;

    svg {
        color: white;
        font-size: 30px;
    }

    &:hover {
        background: ${COLORS.lightOrange};
        transform: rotate(10deg);
    }
`;

const ServiceTitle = styled.h2`
    color: white;
    font-size: 1.5rem;
    margin-bottom: 1rem;
`;

const ServiceDescription = styled.p`
    color: ${COLORS.lightGray};
    margin-bottom: 1.5rem;
    line-height: 1.6;
`;

const FeaturesList = styled.ul`
    list-style: none;
    padding: 0;
    margin-bottom: 2rem;
`;

const FeatureItem = styled.li`
    color: white;
    padding: 0.5rem 0;
    display: flex;
    align-items: center;
    font-size: 0.95rem;

    &:before {
        content: '•';
        color: ${COLORS.orange};
        margin-right: 10px;
    }
`;

const LearnMoreButton = styled.button`
    width: 100%;
    padding: 1rem;
    border: none;
    border-radius: 8px;
    color: white;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    background: ${COLORS.orange};

    &:hover {
        background: ${COLORS.lightOrange};
        transform: translateY(-2px);
    }
`;

export default Services; 